import React from 'react';
import { Card, Progress, Typography, Row, Col, Statistic, Tag } from 'antd';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';

const { Title, Text } = Typography;

// Color palette for charts
const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

// Donut Chart Component
export const DonutChart = ({ data, title, centerText, height = 300 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  return (
    <Card title={title} className="h-full">
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [value, 'Count']} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4">
        {data.map((item, index) => (
          <div key={item.name} className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <Text>{item.name}</Text>
            </div>
            <Text strong>{item.value}</Text>
          </div>
        ))}
      </div>
    </Card>
  );
};

// Bar Chart Component
export const BarChartComponent = ({ data, title, xKey, yKey, height = 300 }) => (
  <Card title={title} className="h-full">
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} />
          <YAxis />
          <Tooltip />
          <Bar dataKey={yKey} fill="#1890ff" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

// Line Chart Component
export const LineChartComponent = ({ data, title, xKey, yKey, height = 300 }) => (
  <Card title={title} className="h-full">
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} />
          <YAxis />
          <Tooltip />
          <Line type="monotone" dataKey={yKey} stroke="#1890ff" strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

// Area Chart Component
export const AreaChartComponent = ({ data, title, xKey, yKey, height = 300 }) => (
  <Card title={title} className="h-full">
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} />
          <YAxis />
          <Tooltip />
          <Area type="monotone" dataKey={yKey} stroke="#1890ff" fill="#1890ff" fillOpacity={0.3} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

// Project Progress Card - Clean, visual design
export const ProjectProgressCard = ({ project }) => {
  const completionRate = project.completionRate || 0;
  
  const getStatusColor = (rate) => {
    if (rate >= 60) return '#52c41a';
    if (rate >= 30) return '#faad14';
    return '#f5222d';
  };

  const getProjectInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'PR';
  };

  return (
    <Card 
      className="h-full hover:shadow-md transition-shadow duration-200"
      bodyStyle={{ padding: '20px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold mr-3"
            style={{ backgroundColor: getStatusColor(completionRate) }}
          >
            {getProjectInitials(project.name)}
          </div>
          <div>
            <Title level={5} className="mb-0 truncate">{project.name}</Title>
            <Text type="secondary" className="text-xs">
              {project.totalHours}h / {project.totalAllocatedHours}h
            </Text>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold" style={{ color: getStatusColor(completionRate) }}>
            {Math.min(completionRate, 100)}%
          </div>
          <Text type="secondary" className="text-xs">Complete</Text>
        </div>
      </div>

      {/* Progress Bar */}
      <Progress 
        percent={Math.min(completionRate, 100)} 
        strokeColor={getStatusColor(completionRate)}
        className="mb-4"
        showInfo={false}
        strokeWidth={8}
      />

      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-2 bg-blue-50 rounded-lg">
          <div className="text-lg font-bold text-blue-600">
            {project.totalHours}
          </div>
          <Text type="secondary" className="text-xs">Hours</Text>
        </div>
        <div className="text-center p-2 bg-purple-50 rounded-lg">
          <div className="text-lg font-bold text-purple-600">
            {project.totalTimesheets}
          </div>
          <Text type="secondary" className="text-xs">Sheets</Text>
        </div>
        <div className="text-center p-2 bg-indigo-50 rounded-lg">
          <div className="text-lg font-bold text-indigo-600">
            {project.teamSize}
          </div>
          <Text type="secondary" className="text-xs">Team</Text>
        </div>
      </div>

      {/* Status */}
      <div className="flex justify-between items-center pt-3 border-t border-gray-100">
        <div className="flex items-center">
          <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
          <Text className="text-xs">{project.approvedTimesheets} Approved</Text>
        </div>
        <div className="flex items-center">
          <div className="w-2 h-2 rounded-full bg-orange-400 mr-2"></div>
          <Text className="text-xs">{project.pendingTimesheets} Pending</Text>
        </div>
      </div>
    </Card>
  );
};

// Employee Performance Card - Clean, visual design
export const EmployeePerformanceCard = ({ employee, isManager = false }) => {
  const allocationPercentage = employee.allocationPercentage || 0;

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'NA';
  };

  const getAvatarColor = (name) => {
    const colors = ['#1890ff', '#52c41a', '#722ed1', '#fa8c16', '#eb2f96', '#13c2c2'];
    const index = name?.length % colors.length || 0;
    return colors[index];
  };

  return (
    <Card 
      className="h-full hover:shadow-md transition-shadow duration-200"
      bodyStyle={{ padding: '20px' }}
    >
      {/* Header with Avatar */}
      <div className="flex items-center mb-4">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold mr-3"
          style={{ backgroundColor: getAvatarColor(employee.name) }}
        >
          {getInitials(employee.name)}
        </div>
        <div className="flex-1 min-w-0">
          <Title level={5} className="mb-0 truncate">{employee.name}</Title>
          <Text type="secondary" className="text-xs truncate block">
            {isManager ? `${employee.position || 'Project Manager'} • ${employee.email}` : employee.email}
          </Text>
        </div>
        <Tag 
          color={
            allocationPercentage > 100 ? 'magenta' : 
            allocationPercentage >= 80 ? 'red' : 
            allocationPercentage >= 60 ? 'orange' : 
            allocationPercentage >= 40 ? 'blue' : 'green'
          }
          className="ml-2"
        >
          {allocationPercentage}%
        </Tag>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">
            {Math.round(employee.totalHours || 0)}
          </div>
          <Text type="secondary" className="text-xs">Hours Logged</Text>
        </div>
        <div className="text-center p-3 bg-purple-50 rounded-lg">
          <div className="text-2xl font-bold text-purple-600">
            {employee.totalTimesheets}
          </div>
          <Text type="secondary" className="text-xs">Timesheets</Text>
        </div>
      </div>

      {/* Bottom Stats */}
      <div className="flex justify-between items-center pt-3 border-t border-gray-100">
        <div className="text-center flex-1">
          <div className="text-sm font-semibold text-gray-700">
            {employee.projectsWorkedOn}
          </div>
          <Text type="secondary" className="text-xs">Projects</Text>
        </div>
        <div className="w-px h-8 bg-gray-200 mx-2"></div>
        <div className="text-center flex-1">
          <div className="text-sm font-semibold text-gray-700">
            {employee.avgHoursPerTimesheet}h
          </div>
          <Text type="secondary" className="text-xs">Avg/Sheet</Text>
        </div>
      </div>
    </Card>
  );
};

// Metric Card Component
export const MetricCard = ({ title, value, prefix, suffix, trend, color = '#1890ff' }) => (
  <Card className="h-full">
    <Statistic
      title={title}
      value={value}
      prefix={prefix}
      suffix={suffix}
      valueStyle={{ color }}
    />
    {trend && (
      <div className="mt-2">
        <Text type="secondary" className="text-sm">
          {trend > 0 ? '↗' : trend < 0 ? '↘' : '→'} {Math.abs(trend)}% from last month
        </Text>
      </div>
    )}
  </Card>
);
